import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { ItemCard, ItemCardCategory } from '../types/itemCard';
import { ItemCardService } from '../services/itemCardService';
import { withImageVersion } from '../utils/imageVersion';

interface ItemCardSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedItemCardIds: string[]) => void;
  userId: string;
  classId: string;
  initialSelectedIds?: string[];
  categoryFilter?: ItemCardCategory[];
}

const ItemCardSelectionModal: React.FC<ItemCardSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userId,
  classId,
  initialSelectedIds = [],
  categoryFilter
}) => {
  const [activeTab, setActiveTab] = useState<ItemCardCategory>('levelup');
  const [levelupItems, setLevelupItems] = useState<ItemCard[]>([]);
  const [assignmentItems, setAssignmentItems] = useState<ItemCard[]>([]);
  const [storeItems, setStoreItems] = useState<ItemCard[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set(initialSelectedIds));
  const [isLoading, setIsLoading] = useState(false);
  const [itemCardService, setItemCardService] = useState<ItemCardService | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const lastClickTimeRef = useRef<number>(0);
  const lastClickItemIdRef = useRef<string | null>(null);

  // 서비스 초기화
  useEffect(() => {
    if (userId && classId) {
      setItemCardService(new ItemCardService(userId, classId));
    }
  }, [userId, classId]);

  // 데이터 로드
  const loadData = useCallback(async () => {
    if (!itemCardService) return;

    setIsLoading(true);
    try {
      const data = await itemCardService.loadItemCardSet();
      if (data) {
        setLevelupItems(data.levelupItem);
        setAssignmentItems(data.assignmentItem);
        setStoreItems(data.storeItem);
      }
    } catch (error) {
      console.error('아이템 카드 데이터 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [itemCardService]);

  // 초기 데이터 로드
  useEffect(() => {
    if (isOpen && itemCardService) {
      loadData();
      setSelectedItemIds(new Set(initialSelectedIds));
      setImageErrors(new Set()); // 모달 열릴 때 이미지 에러 초기화
    }
  }, [isOpen, itemCardService, initialSelectedIds, loadData]);

  // 이미지 로드 실패 핸들러
  const handleImageError = (itemCardId: string) => {
    setImageErrors(prev => new Set(prev).add(itemCardId));
  };

  // categoryFilter가 있을 때 첫 번째 필터된 탭을 기본값으로 설정
  useEffect(() => {
    if (categoryFilter && categoryFilter.length > 0) {
      setActiveTab(categoryFilter[0]);
    }
  }, [categoryFilter]);

  // 모달 열릴 때 배경 스크롤 비활성화
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // 아이템 카드 선택/해제
  const toggleItemSelection = (itemCardId: string) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemCardId)) {
        newSet.delete(itemCardId);
      } else {
        newSet.add(itemCardId);
      }
      return newSet;
    });
  };

  // 개별 아이템 카드 클릭 (더블 클릭 시 즉시 적용 후 모달 닫기)
  const handleItemClick = (itemCardId: string) => {
    const now = Date.now();
    const lastId = lastClickItemIdRef.current;
    const lastTime = lastClickTimeRef.current;
    const isDoubleClick = lastId === itemCardId && now - lastTime < 350;

    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemCardId)) {
        newSet.delete(itemCardId);
      } else {
        newSet.add(itemCardId);
      }

      if (isDoubleClick) {
        // 현재 클릭으로 반영된 선택 상태(newSet)를 기준으로 즉시 적용
        onConfirm(Array.from(newSet));
        onClose();
      }

      return newSet;
    });

    if (isDoubleClick) {
      lastClickItemIdRef.current = null;
      lastClickTimeRef.current = 0;
    } else {
      lastClickItemIdRef.current = itemCardId;
      lastClickTimeRef.current = now;
    }
  };

  // 확인 버튼 클릭
  const handleConfirm = () => {
    onConfirm(Array.from(selectedItemIds));
    onClose();
  };

  // 전체 선택/해제
  const toggleAllSelection = () => {
    const currentItems = activeTab === 'levelup' ? levelupItems : 
                        activeTab === 'assignment' ? assignmentItems : storeItems;
    const currentItemIds = currentItems.map(item => item.itemCardId);
    const allSelected = currentItemIds.every(id => selectedItemIds.has(id));
    
    if (allSelected) {
      // 모든 아이템 해제
      setSelectedItemIds(prev => {
        const newSet = new Set(prev);
        currentItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // 모든 아이템 선택
      setSelectedItemIds(prev => {
        const newSet = new Set(prev);
        currentItemIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  if (!isOpen) return null;

  const currentItems = activeTab === 'levelup' ? levelupItems : 
                      activeTab === 'assignment' ? assignmentItems : storeItems;
  const currentItemIds = currentItems.map(item => item.itemCardId);
  const allSelected = currentItemIds.length > 0 && currentItemIds.every(id => selectedItemIds.has(id));

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>과제 보상 아이템 카드 선택</h3>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <TabContainer>
          {(!categoryFilter || categoryFilter.includes('levelup')) && (
            <TabButton
              $isActive={activeTab === 'levelup'}
              onClick={() => setActiveTab('levelup')}
            >
              레벨업 아이템
            </TabButton>
          )}
          {(!categoryFilter || categoryFilter.includes('assignment')) && (
            <TabButton
              $isActive={activeTab === 'assignment'}
              onClick={() => setActiveTab('assignment')}
            >
              과제보상 아이템
            </TabButton>
          )}
          {(!categoryFilter || categoryFilter.includes('store')) && (
            <TabButton
              $isActive={activeTab === 'store'}
              onClick={() => setActiveTab('store')}
            >
              스토어 아이템
            </TabButton>
          )}
        </TabContainer>

        <ContentArea>
          <SelectionHeader>
            <SelectionInfo>
              선택된 아이템: {selectedItemIds.size}개
            </SelectionInfo>
            <SelectAllButton onClick={toggleAllSelection}>
              {allSelected ? '전체 해제' : '전체 선택'}
            </SelectAllButton>
          </SelectionHeader>

          {isLoading ? (
            <LoadingText>로딩 중...</LoadingText>
          ) : (
            <ItemCardGrid>
              {currentItems.map((item) => (
                <ItemCardBlock
                  key={item.itemCardId}
                  $isSelected={selectedItemIds.has(item.itemCardId)}
                  onClick={() => handleItemClick(item.itemCardId)}
                >
                  <SelectionCheckbox $isSelected={selectedItemIds.has(item.itemCardId)}>
                    {selectedItemIds.has(item.itemCardId) && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="check-icon">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
                      </svg>
                    )}
                  </SelectionCheckbox>
                  
                  <IconArea>
                    {item.cardImage === 'default' || imageErrors.has(item.itemCardId) ? (
                      <PlaceholderIcon>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                          <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                          <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                        </svg>
                      </PlaceholderIcon>
                    ) : item.cardImage && /^\d+$/.test(item.cardImage) ? (
                      <img src={`/CardIcon/IconBasic/IconBasic-${item.cardImage}.png`} alt={item.name} onError={() => handleImageError(item.itemCardId)} />
                    ) : item.cardImage ? (
                      <img src={withImageVersion(item.cardImage, item.updatedAt)} alt={item.name} onError={() => handleImageError(item.itemCardId)} />
                    ) : item.icon ? (
                      <img src={withImageVersion(item.icon, item.updatedAt)} alt={item.name} onError={() => handleImageError(item.itemCardId)} />
                    ) : (
                      <PlaceholderIcon>
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                          <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                      </PlaceholderIcon>
                    )}
                  </IconArea>
                  
                  <ItemName>{item.name}</ItemName>
                  <ItemMemo>{item.memo}</ItemMemo>
                  
                  {(item.category === 'store' && item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined) ? (
                    <ItemMetaGroup>
                      {item.category === 'store' && item.cost !== 'Nocharge' && (
                        <ItemCost>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                            <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                            <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                          </svg>
                          <span>X{item.cost}</span>
                        </ItemCost>
                      )}
                      {item.type === 'active' && item.useCount !== undefined && (
                        <UseCountBadge>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                          </svg>
                          <span>X{item.useCount}</span>
                        </UseCountBadge>
                      )}
                    </ItemMetaGroup>
                  ) : null}
                </ItemCardBlock>
              ))}
            </ItemCardGrid>
          )}
        </ContentArea>

        <ButtonGroup>
          <CancelButton data-variant="modal-secondary" onClick={onClose}>취소</CancelButton>
          <ConfirmButton onClick={handleConfirm}>
            확인 ({selectedItemIds.size}개 선택)
          </ConfirmButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ItemCardSelectionModal;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10001;
  padding: 80px 20px;
  overflow-y: auto;
  min-height: 100vh;
  
  @media (max-width: 600px) {
    padding: 60px 20px;
  }
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  overflow: visible;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10002;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$isActive ? 'white' : 'var(--color-text-secondary)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: -2px;
  }
`;

const ContentArea = styled.div`
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const SelectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border);
`;

const SelectionInfo = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

const SelectAllButton = styled.button`
  padding: 8px 16px;
  min-width: 80px;
  min-height: 36px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--color-text-gray);
`;

const ItemCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 16px;
  justify-content: center;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const ItemCardBlock = styled.div<{ $isSelected: boolean }>`
  width: 120px;
  height: 200px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-strong)'};
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  /* 다크 모드: 흰색 테두리 (선택 여부와 관계없이 흰색 유지) */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SelectionCheckbox = styled.div<{ $isSelected: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'transparent'};
  border: ${props => props.$isSelected ? '2px solid var(--color-accent-strong)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1;

  /* 다크 모드: 흰색 원형 배경 */
  :root:not([data-theme='light']) & {
    background: ${props => props.$isSelected ? 'rgba(255, 255, 255, 1)' : 'transparent'};
    border: ${props => props.$isSelected ? '2px solid rgba(255, 255, 255, 1)' : 'none'};

    /* 다크 모드: 체크 아이콘을 진한 회색으로 */
    svg path {
      fill: #333333;
    }
  }
`;

const IconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const ItemMetaGroup = styled.div`
  width: 100%;
  margin-top: 6%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
`;

const ItemCost = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const UseCountBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  min-width: 100px;
  min-height: 48px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  min-width: 100px;
  min-height: 48px;
  background: var(--color-accent-strong);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;
